import { API_CONFIG } from '../utils/constants.js';
import { STORAGE_KEYS } from '../utils/constants.js';

const DEFAULT_RETRY_DELAY = 5000; // 재연결 기본 대기 시간(5초)
const MAX_RETRY_DELAY = 600000; // 최대 재연결 대기 시간(600초)

// userSysId, 토큰을 이용해 최종 SSE 연결 URL을 만든다.
const buildSseUrl = (userSysId, token) => {
  const base = (API_CONFIG.BASE_URL || '').replace(/\/$/, '');
  const path = `/drivecore/service/sse/connect/${userSysId}`;
  const url = `${base}${path}`;

  if (!token) {
    return url;
  }

  // const params = new URLSearchParams({ accessToken: token });
  // return `${url}&${params.toString()}`;
  return url;
};

// SSE 연결 전용 클라이언트 클래스
class SystemNotificationSseClient {
  constructor() {
    this.eventSource = null; // 실질적인 EventSource 인스턴스
    this.listeners = new Set(); // 메시지 리스너
    this.statusListeners = new Set(); // 상태 변화 리스너
    this.currentUserSysId = null;
    this.currentToken = null;
    this.retryTimeout = null; // 재연결 타이머
    this.retryCount = 0; // 재연결 시도 횟수
    this.lastConnectionTime = null; // 마지막 연결 시간
  }

  get hasListeners() {
    return this.listeners.size > 0 || this.statusListeners.size > 0;
  }

  emitStatus(status, error) {
    this.statusListeners.forEach((cb) => {
      try {
        cb(status, error ?? null);
      } catch (err) {
        console.error('[SSE] status listener error', err);
      }
    });
  }

  emitMessage(message) {
    this.listeners.forEach((cb) => {
      try {
        cb(message);
      } catch (err) {
        console.error('[SSE] message listener error', err);
      }
    });
  }

  // 메시지 처리 공통 함수
  handleMessage(event, eventType) {
    const basePayload = {
      id: event.lastEventId || `${Date.now()}-${Math.random()}`,
      type: eventType || event.type || 'message',
      raw: event.data,
      receivedAt: new Date().toISOString(),
    };

    let parsed;
    try {
      parsed = event.data ? JSON.parse(event.data) : null;
    } catch (err) {
      console.warn('[SSE][handleMessage] JSON parse 실패, 원본 문자열 반환', err);
      parsed = event.data;
    }

    this.emitMessage({ ...basePayload, payload: parsed });
  }

  // 재연결 예약 (이미 타이머가 있으면 중복 방지)
  scheduleReconnect(userSysId, token) {
    if (this.retryTimeout) {
      return;
    }

    // Exponential backoff: 재연결 횟수가 많을수록 대기 시간 증가 (최대 60초)
    const delay = Math.min(
      DEFAULT_RETRY_DELAY * Math.pow(2, this.retryCount),
      MAX_RETRY_DELAY
    );

    this.retryCount++;

    this.retryTimeout = setTimeout(() => {
      this.retryTimeout = null;
      this.connect(userSysId, token);
    }, delay);
  }

  // 재연결 횟수 리셋
  resetRetryCount() {
    this.retryCount = 0;
  }

  clearReconnectTimer() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
  }

  // 이벤트 리스너 등록 (재사용 가능)
  registerEventListeners() {
    if (!this.eventSource) {
      console.warn('[SSE] EventSource가 없어서 리스너를 등록할 수 없습니다.');
      return;
    }

    // 이미 리스너가 등록되어 있으면 중복 등록 방지
    if (this.eventSource._listenersRegistered) {
      return;
    }


    // 기본 'message' 이벤트 처리
    this.eventSource.onmessage = (event) => {
      this.handleMessage(event, 'message');
    };

    // 알려진 커스텀 이벤트 타입들 (대소문자 모두 처리)
    const customEventTypes = ['ALERT', 'INTERACTION', 'NOTIFICATION', 'ALARM'];
    customEventTypes.forEach((eventType) => {
      // 소문자로 등록
      this.eventSource.addEventListener(eventType.toLowerCase(), (event) => {
        this.handleMessage(event, eventType);
      });
      // 대문자로도 등록 (서버가 대문자로 보낼 수 있음)
      if (eventType !== eventType.toLowerCase()) {
        this.eventSource.addEventListener(eventType, (event) => {
          this.handleMessage(event, eventType);
        });
      }
    });

    this.eventSource._listenersRegistered = true;
  }

  // SSE 연결 시도
  async connect(userSysId, token) {
    if (!userSysId) {
      console.warn('[SSE] Missing userSysId. Connection aborted.');
      return;
    }

    const storedToken =
      token || localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) || undefined;

    const shouldReconnect =
      !this.eventSource ||
      this.eventSource.readyState === EventSource.CLOSED ||
      this.currentUserSysId !== userSysId ||
      this.currentToken !== storedToken;

    // 이미 연결되어 있고 같은 사용자/토큰이면 리스너만 재등록
    if (this.eventSource && 
        this.eventSource.readyState === EventSource.OPEN &&
        this.currentUserSysId === userSysId &&
        this.currentToken === storedToken) {
      this.registerEventListeners();
      return;
    }

    if (!shouldReconnect) {
      return;
    }

    this.disconnect();

    const url = buildSseUrl(userSysId, storedToken);
    this.currentUserSysId = userSysId;
    this.currentToken = storedToken;

    try {
      this.emitStatus('connecting');
      
      // EventSource는 헤더를 설정할 수 없으므로, fetch API를 사용하여 스트림 읽기
      // 헤더에 토큰을 포함할 수 있음
      const headers = {
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache',
      };
      
      if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }
      
      // fetch를 사용하여 스트림 읽기
      const response = await fetch(url, {
        method: 'GET',
        headers,
        credentials: 'include', // 쿠키 포함
      });

      if (!response.ok) {
        throw new Error(`SSE 연결 실패: ${response.status} ${response.statusText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      
      // EventSource와 호환되는 인터페이스를 만들기 위해 커스텀 EventSource 구현
      this.eventSource = {
        url,
        readyState: EventSource.CONNECTING,
        withCredentials: true,
        close: () => {
          reader.cancel();
          this.eventSource.readyState = EventSource.CLOSED;
        },
        addEventListener: (type, handler) => {
          if (!this.eventSource._listeners) {
            this.eventSource._listeners = {};
          }
          if (!this.eventSource._listeners[type]) {
            this.eventSource._listeners[type] = [];
          }
          this.eventSource._listeners[type].push(handler);
        },
        _listeners: {},
        onmessage: null,
        _emit: (event) => {
          // onmessage 핸들러 호출 (message 타입일 때만)
          if (event.type === 'message' && this.eventSource.onmessage) {
            this.eventSource.onmessage(event);
          }
          // addEventListener로 등록된 핸들러 호출 (해당 타입만)
          if (this.eventSource._listeners[event.type]) {
            this.eventSource._listeners[event.type].forEach(handler => {
              handler(event);
            });
          }
          // 커스텀 이벤트 타입은 message로도 호출하지 않음 (중복 방지)
        }
      };

      // 연결 성공 처리
      this.eventSource.readyState = EventSource.OPEN;
      this.clearReconnectTimer();
      this.resetRetryCount();
      this.lastConnectionTime = Date.now();
      
      if (typeof window !== 'undefined') {
        window.__sseEventSource = this.eventSource;
        window.__sseClient = this;
      }
      this.emitStatus('open');

      // 스트림 읽기 시작
      const readStream = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              this.eventSource.readyState = EventSource.CLOSED;
              this.emitStatus('closed');
              break;
            }

            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split('\n');
            
            let eventType = 'message';
            let eventData = '';
            let eventId = null;

            for (const line of lines) {
              if (line.startsWith('event:')) {
                eventType = line.substring(6).trim();
              } else if (line.startsWith('data:')) {
                eventData += line.substring(5).trim() + '\n';
              } else if (line.startsWith('id:')) {
                eventId = line.substring(3).trim();
              } else if (line === '') {
                // 빈 줄 = 이벤트 종료
                if (eventData) {
                  const event = {
                    type: eventType,
                    data: eventData.trim(),
                    lastEventId: eventId,
                  };
                  this.eventSource._emit(event);
                }
                eventType = 'message';
                eventData = '';
                eventId = null;
              }
            }
          }
        } catch (error) {
          if (this.eventSource) {
            this.eventSource.readyState = EventSource.CLOSED;
          }
          this.emitStatus('error', error);
          this.disconnect(false);
          this.scheduleReconnect(userSysId, storedToken);
        }
      };

      // 스트림 읽기 시작 (비동기로 실행)
      readStream();
      
      // fetch 기반에서는 onopen/onerror가 필요 없음 (이미 위에서 처리)

      this.registerEventListeners();
    } catch (error) {
      this.emitStatus('error', error);
      this.scheduleReconnect(userSysId, storedToken);
    }
  }

  // SSE 연결 종료
  disconnect(resetState = true) {
    this.clearReconnectTimer();
    if (this.eventSource) {
      this.eventSource.close();
      // 리스너 등록 플래그도 리셋
      if (this.eventSource._listenersRegistered) {
        delete this.eventSource._listenersRegistered;
      }
      this.eventSource = null;
      if (resetState) {
        this.resetRetryCount(); // 명시적 종료 시 재연결 횟수 리셋
        this.emitStatus('closed');
      }
    }
  }

  // 메시지 리스너 등록
  subscribe(listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
      // 리스너가 없어도 연결은 유지 (다른 컴포넌트에서 사용할 수 있음)
      // 단, 명시적으로 disconnect가 호출될 때만 연결 종료
    };
  }

  // 연결 상태 리스너 등록
  onStatusChange(listener) {
    this.statusListeners.add(listener);
    return () => {
      this.statusListeners.delete(listener);
    };
  }
}

export const systemNotificationSseClient = new SystemNotificationSseClient();
